/**
 * MedicalInfoStep.js — Assessment Wizard, Screen 6 "Medical Information"
 *
 * Captures conditions that constrain food selection and safety rules.
 * Each condition maps to specific exclusion rules in the Rule Engine
 * downstream (e.g. Diabetes → limit high-glycemic-index foods, Kidney
 * Disease → limit high-potassium/phosphorus foods) — this screen only
 * collects the selections, it doesn't apply the rules itself.
 *
 * Built on the shared AssessmentStepShell so the progress bar, step
 * counter, question header, and Previous/Next footer stay identical to
 * every other wizard screen.
 *
 * 'None' is mutually exclusive with every real condition: selecting it
 * clears everything else (and hides + clears Current Medications, per
 * spec); selecting any real condition clears 'None'.
 *
 * Risk logic (FSD v2.1 §4/§5, DEC-17 High-Risk Blocking Logic): each
 * condition in MedicalInfoStep.constants.js carries a riskTier. Two
 * independent flags come out of that, both forwarded to onNext for the
 * plan-generation stage (UC-02) to act on — this screen never decides
 * block/warn itself, it only surfaces the deterministic tier data:
 *
 *   - riskFlag: true when a professional_review-tier condition is
 *     selected, or when 2+ caution/professional_review conditions are
 *     selected together (their combined rules may not reconcile
 *     deterministically — §5). Shows RiskNotice, does NOT block Next.
 *   - planBlocked: true when a BLOCK-tier condition (currently only
 *     "eating_disorder_concern") is selected. Per DEC-17 this must
 *     prevent a personalized weight-loss/calorie-deficit plan at
 *     generation time — but the assessment itself still completes, so
 *     Next stays enabled here; this screen shows a supportive,
 *     non-diagnostic SupportNotice instead of blocking progress.
 */

import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import AssessmentStepShell from "../Assesment/AssessmentStepShell";
import useMedicalAnalytics from "./useMedicalAnalytics";
import { validateMedical } from "./MedicalInfoStep.validation";
import { TOTAL_STEPS, STEP_NUMBERS } from "../Assesment/assessmentConfig";
import {
  CONDITION_OPTIONS,
  NONE_CONDITION_ID,
  MEDICATIONS_MAX_LENGTH,
  MEDICAL_INITIAL_DATA,
  CAUTION_CONDITION_IDS,
  PROFESSIONAL_REVIEW_CONDITION_IDS,
  BLOCKING_CONDITION_IDS,
} from "./MedicalInfoStep.constants";
import {
  FieldGroup,
  FieldLabel,
  RequiredMark,
  HelperText,
  CardGrid,
  ConditionCard,
  CheckIcon,
  TextArea,
  CharCountRow,
  CharCount,
  RiskNotice,
  SupportNotice,
} from "./MedicalInfoStep.styles";

const STEP_NUMBER = STEP_NUMBERS.MEDICAL;

/**
 * @param {object}   props
 * @param {string}   props.sessionId     - Anonymous session id from Screen 1.
 * @param {object}   [props.initialData] - Previously entered values, if the
 *   user navigated back to this step.
 * @param {function} props.onPrevious    - Go to Screen 5 (Activity Level).
 * @param {function} props.onNext        - Go to Screen 7, receives the full
 *   medical data object ({ conditions, medications, riskFlag, planBlocked,
 *   riskConditionIds }). planBlocked signals plan generation (UC-02) must
 *   not produce a personalized weight-loss/calorie-deficit plan for this
 *   profile (DEC-17).
 */
const MedicalInfoStep = ({ sessionId, initialData = null, onPrevious, onNext }) => {
  const analytics = useMedicalAnalytics();
  const [formData, setFormData] = useState({ ...MEDICAL_INITIAL_DATA, ...initialData });

  const { isValid, errors } = useMemo(() => validateMedical(formData), [formData]);

  const selectedCautionConditions = useMemo(
    () => formData.conditions.filter((id) => CAUTION_CONDITION_IDS.includes(id)),
    [formData.conditions]
  );
  const selectedProfessionalReviewConditions = useMemo(
    () => formData.conditions.filter((id) => PROFESSIONAL_REVIEW_CONDITION_IDS.includes(id)),
    [formData.conditions]
  );
  const selectedBlockingConditions = useMemo(
    () => formData.conditions.filter((id) => BLOCKING_CONDITION_IDS.includes(id)),
    [formData.conditions]
  );

  const riskConditions = useMemo(
    () => [...selectedProfessionalReviewConditions, ...selectedCautionConditions],
    [selectedProfessionalReviewConditions, selectedCautionConditions]
  );
  // Single stable condition with an approved rule set only WARNs; 2+
  // caution/professional_review conditions, or any professional_review
  // condition on its own, escalates to the professional-review notice.
  const riskFlag =
    selectedProfessionalReviewConditions.length >= 1 || riskConditions.length >= 2;
  const planBlocked = selectedBlockingConditions.length > 0;
  const hasNone = formData.conditions.includes(NONE_CONDITION_ID);

  // Fire each flag's event once per transition into "true", not on every
  // render while it stays true — mirrors Welcome.js's
  // trustMarkersFiredRef guard pattern.
  const riskFlagFiredRef = useRef(false);
  useEffect(() => {
    if (riskFlag && !riskFlagFiredRef.current) {
      riskFlagFiredRef.current = true;
      analytics.trackRiskFlagSet(sessionId, riskConditions);
    } else if (!riskFlag) {
      riskFlagFiredRef.current = false;
    }
  }, [riskFlag, analytics, sessionId, riskConditions]);

  const planBlockedFiredRef = useRef(false);
  useEffect(() => {
    if (planBlocked && !planBlockedFiredRef.current) {
      planBlockedFiredRef.current = true;
      analytics.trackPlanBlockFlagSet(sessionId, selectedBlockingConditions);
    } else if (!planBlocked) {
      planBlockedFiredRef.current = false;
    }
  }, [planBlocked, analytics, sessionId, selectedBlockingConditions]);

  const handleConditionToggle = useCallback(
    (conditionId) => {
      const wasSelected = formData.conditions.includes(conditionId);

      setFormData((prev) => {
        let conditions;

        if (conditionId === NONE_CONDITION_ID) {
          // 'None' clears every other condition; unselecting it just
          // removes itself.
          conditions = wasSelected ? [] : [NONE_CONDITION_ID];
        } else if (wasSelected) {
          conditions = prev.conditions.filter((id) => id !== conditionId);
        } else {
          // Picking a real condition always clears 'None' — they're
          // mutually exclusive.
          conditions = [
            ...prev.conditions.filter((id) => id !== NONE_CONDITION_ID),
            conditionId,
          ];
        }

        const nowHasNone = conditions.includes(NONE_CONDITION_ID);
        return {
          ...prev,
          conditions,
          // Medications is hidden once 'None' is selected — clear it too
          // so stale text can't linger in the submitted record.
          medications: nowHasNone ? "" : prev.medications,
        };
      });

      analytics.trackConditionToggle(sessionId, conditionId, !wasSelected);
    },
    [analytics, sessionId, formData.conditions]
  );

  const handleMedicationsChange = useCallback(
    (e) => {
      const value = e.target.value.slice(0, MEDICATIONS_MAX_LENGTH);
      setFormData((prev) => ({ ...prev, medications: value }));
      analytics.trackMedicationsInput(sessionId, value.length);
    },
    [analytics, sessionId]
  );

  const handlePrevious = useCallback(() => {
    // Previous from Medical Info (Screen 6) returns to Activity Level (Screen 5).
    analytics.trackStepPrevious(sessionId);
    onPrevious?.();
  }, [analytics, sessionId, onPrevious]);

  const handleNext = useCallback(() => {
    if (!isValid) {
      // Defensive guard — Next is already disabled via nextDisabled below,
      // this only fires if something calls onNext programmatically.
      analytics.trackValidationBlocked(sessionId, errors);
      return;
    }

    analytics.trackStepNext(sessionId, formData, riskFlag, planBlocked);
    // Advances the wizard to Screen 7 (Food Preferences) — the wizard's
    // step router decides which screen renders next. planBlocked and
    // riskConditionIds ride along so the plan-generation stage (UC-02)
    // can apply DEC-17 without re-deriving tiers from raw condition ids.
    onNext?.({ ...formData, riskFlag, planBlocked, riskConditionIds: riskConditions });
  }, [analytics, sessionId, formData, isValid, errors, riskFlag, planBlocked, riskConditions, onNext]);

  return (
    <AssessmentStepShell
      step={STEP_NUMBER}
      totalSteps={TOTAL_STEPS}
      title="Medical information"
      description="This helps us keep your plan safe and tailored to any conditions you manage."
      onPrevious={handlePrevious}
      onNext={handleNext}
      nextDisabled={!isValid}
    >
      <FieldGroup>
        <FieldLabel id="conditionsLabel">
          Conditions
          <RequiredMark>*</RequiredMark>
        </FieldLabel>
        <CardGrid role="group" aria-labelledby="conditionsLabel">
          {CONDITION_OPTIONS.map((opt) => {
            const active = formData.conditions.includes(opt.id);
            return (
              <ConditionCard
                key={opt.id}
                type="button"
                role="checkbox"
                aria-checked={active}
                $active={active}
                onClick={() => handleConditionToggle(opt.id)}
              >
                <CheckIcon $active={active} aria-hidden="true">
                  {active ? "✓" : ""}
                </CheckIcon>
                {opt.label}
              </ConditionCard>
            );
          })}
        </CardGrid>
      </FieldGroup>

      {!hasNone && (
        <FieldGroup>
          <FieldLabel htmlFor="medications">
            Current medications{" "}
            <span style={{ fontWeight: 400, color: "var(--c-text-muted)" }}>(optional)</span>
          </FieldLabel>
          <TextArea
            id="medications"
            placeholder="e.g. Metformin 500mg twice daily"
            value={formData.medications}
            onChange={handleMedicationsChange}
            maxLength={MEDICATIONS_MAX_LENGTH}
          />
          <CharCountRow>
            <CharCount $over={formData.medications.length >= MEDICATIONS_MAX_LENGTH}>
              {formData.medications.length}/{MEDICATIONS_MAX_LENGTH}
            </CharCount>
          </CharCountRow>
          <HelperText>
            Shown to you as context only — it isn't checked for drug interactions. Please
            confirm any dietary changes with your doctor or pharmacist.
          </HelperText>
        </FieldGroup>
      )}

      {riskFlag && (
        <RiskNotice role="status">
          You've selected multiple conditions that need extra care together. We'll apply
          stricter safety rules to your plan and recommend checking in with your doctor
          before making major dietary changes.
        </RiskNotice>
      )}

      {planBlocked && (
        <SupportNotice role="status">
          Thanks for sharing that. We won't generate a calorie-restricted or weight-loss
          plan based on this — instead we'll focus your plan on general balanced nutrition,
          and we'd really encourage you to work with a doctor or registered dietitian who
          can support you directly.
        </SupportNotice>
      )}
    </AssessmentStepShell>
  );
};

MedicalInfoStep.displayName = "MedicalInfoStep";
export default React.memo(MedicalInfoStep);